import { BookOpen, Users, Languages, ShieldCheck, Sparkles, Menu, X, ArrowRight, ChevronRight, Sprout } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import heroDesktop from "./assets/hero-sprout-desktop.png";
import { SCROLL_CONFIG, SECTIONS } from "./lib/constants";

// ─── Single global reveal hook ────────────────────────────────────────────────
// One IntersectionObserver per mount — observes all .reveal* children at once.
function useReveal() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = root.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); });
      },
      { threshold: SCROLL_CONFIG.revealThreshold, rootMargin: SCROLL_CONFIG.revealMargin },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ─── Merged Card component ────────────────────────────────────────────────────
type CardVariant = "info" | "feature";

function Card({
  variant = "info",
  icon,
  iconBg = "bg-white/15",
  title,
  body,
  className = "",
  light = false,
}: {
  variant?: CardVariant;
  icon: React.ReactNode;
  iconBg?: string;
  title: string;
  body: string;
  className?: string;
  light?: boolean;
}) {
  const isFeature = variant === "feature";
  return (
    <div
      className={`rounded-2xl p-3 shadow-lg backdrop-blur-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1
        ${isFeature
          ? `w-[150px] sm:w-[220px] sm:p-4 ${light ? "border border-white/25 bg-white/15" : "border border-border/50 bg-card/80"}`
          : "flex h-full flex-col border border-white/20 bg-white/10 p-5 shadow-sm hover:bg-white/15 hover:border-green-400/40 hover:shadow-lg"
        } ${className}`}
    >
      <div className={`flex ${isFeature ? "items-start gap-3" : "flex-col"}`}>
        <div className={`flex shrink-0 items-center justify-center rounded-lg ${iconBg}
          ${isFeature
            ? "h-8 w-8 sm:h-9 sm:w-9"
            : "h-10 w-10 text-green-300 transition-colors group-hover:bg-green-500/30 group-hover:text-green-200"
          }`}
        >
          {icon}
        </div>
        <div className={isFeature ? "min-w-0" : "mt-4"}>
          <div className={`font-semibold ${isFeature ? "text-sm" : "text-base"} ${light || !isFeature ? "text-white" : "text-foreground"}`}>
            {title}
          </div>
          <p className={`mt-1 leading-snug ${isFeature ? "text-xs" : "mt-2 text-sm"} ${light || !isFeature ? "text-white/65" : "text-muted-foreground"}`}>
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export function App() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const goTo = useCallback((id: string) => {
    const el = document.getElementById(`section-${id}`);
    const scroller = scrollerRef.current;
    if (el && scroller) scroller.scrollTo({ left: el.offsetLeft, behavior: "smooth" });
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let rafWheel: number | undefined;
    let rafScroll: number | undefined;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (rafWheel) cancelAnimationFrame(rafWheel);
      rafWheel = requestAnimationFrame(() => {
        const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
        scroller.scrollLeft += delta * SCROLL_CONFIG.wheelMultiplier;
      });
    };

    const onScroll = () => {
      if (rafScroll) cancelAnimationFrame(rafScroll);
      rafScroll = requestAnimationFrame(() => {
        const x = scroller.scrollLeft + scroller.clientWidth / 2;
        let current = SECTIONS[0].id as string;
        for (const s of SECTIONS) {
          const el = document.getElementById(`section-${s.id}`);
          if (el && el.offsetLeft <= x) current = s.id;
        }
        setActive(current);
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        if (maxScroll > 0) setScrollProgress(scroller.scrollLeft / maxScroll);
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const w = scroller.clientWidth;
      if (e.key === "ArrowRight" || e.key === "PageDown") {
        e.preventDefault();
        scroller.scrollBy({ left: w, behavior: "smooth" });
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        scroller.scrollBy({ left: -w, behavior: "smooth" });
      }
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    scroller.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      scroller.removeEventListener("wheel", onWheel);
      scroller.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKeyDown);
      if (rafWheel) cancelAnimationFrame(rafWheel);
      if (rafScroll) cancelAnimationFrame(rafScroll);
    };
  }, []);

  return (
    <div className="relative flex h-[100dvh] flex-col overflow-hidden font-sans text-foreground">
      <BackgroundLayer scrollRef={scrollerRef} />

      <header className="absolute top-0 left-0 right-0 z-30 mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-5 lg:px-10">
        <button onClick={() => goTo("home")} className="group flex items-center gap-2 text-lg font-medium tracking-tight sm:text-xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-brand-foreground text-sm font-bold">
            <Sprout className="h-5 w-5" />
          </span>
          <span className="text-white">OpenLearn</span> <span className="font-semibold text-green-300">AI</span>
        </button>
        <nav className="hidden items-center gap-7 text-sm text-white/80 md:flex lg:gap-9">
          {SECTIONS.slice(1).map((s) => (
            <button
              key={s.id}
              onClick={() => goTo(s.id)}
              className={`relative transition hover:text-white ${active === s.id ? "text-white font-medium" : ""}`}
            >
              {s.label}
              {active === s.id && <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-brand" />}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => goTo("community")}
            className="hidden rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition hover:bg-black/80 md:inline-flex"
          >
            Join the Movement
          </button>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5 text-white" /> : <Menu className="h-5 w-5 text-white" />}
          </button>
        </div>

        <div className={`absolute left-0 right-0 top-full mx-4 mt-2 overflow-hidden rounded-2xl border border-white/20 bg-black/70 p-3 shadow-xl backdrop-blur-xl transition-all duration-300 md:hidden ${menuOpen ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-2"}`}>
          {SECTIONS.slice(1).map((s) => (
            <button
              key={s.id}
              onClick={() => goTo(s.id)}
              className={`block w-full rounded-lg px-4 py-3 text-left text-sm text-white/80 transition hover:bg-white/10 ${active === s.id ? "bg-white/10 font-medium text-white" : ""}`}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={() => goTo("community")}
            className="mt-2 block w-full rounded-full bg-black px-4 py-3 text-center text-sm font-medium text-white"
          >
            Join the Movement
          </button>
        </div>
      </header>

      <div
        ref={scrollerRef}
        className="hide-scrollbar flex flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth"
      >
        <SectionHome id="section-home" onCta={() => goTo("mission")} />
        <SectionMission id="section-mission" />
        <SectionEducators id="section-educators" />
        <SectionLearners id="section-learners" />
        <SectionCommunity id="section-community" />
        <SectionAbout id="section-about" />
      </div>

      {/* Progress bar */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 h-0.5 bg-white/10">
        <div className="h-full bg-green-400 transition-all duration-150 ease-out" style={{ width: `${scrollProgress * 100}%` }} />
      </div>

      {/* Dot nav */}
      <div className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 sm:bottom-5">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/20 bg-black/30 px-3 py-2 shadow-sm backdrop-blur-md">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => goTo(s.id)}
              aria-label={`Go to ${s.label}`}
              className={`h-2 rounded-full transition-all duration-300 ${active === s.id ? "w-6 bg-green-400" : "w-2 bg-white/40 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>

      {active === "home" && (
        <div className="pointer-events-none absolute bottom-14 right-6 z-20 animate-fade-in-right sm:bottom-16 sm:right-10">
          <div className="flex items-center gap-2 rounded-full bg-black/30 px-4 py-2 text-xs text-white/70 shadow-sm backdrop-blur-md">
            <span>Scroll to explore</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Background ───────────────────────────────────────────────────────────────
function BackgroundLayer({ scrollRef }: { scrollRef: React.RefObject<HTMLDivElement | null> }) {
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    let raf: number | undefined;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (imgRef.current) {
          const progress = scroller.scrollLeft / (scroller.scrollWidth - scroller.clientWidth || 1);
          imgRef.current.style.transform = `translateX(${-progress * SCROLL_CONFIG.parallaxDepth}%) scale(1.15)`;
        }
      });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef]);

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div ref={imgRef} className="parallax-bg absolute inset-0 h-full w-[120%]">
        <img src={heroDesktop} alt="" className="h-full w-full object-cover object-center" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/10 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, children }: { id: string; children: React.ReactNode }) {
  const revealRef = useReveal();
  return (
    <section
      id={id}
      ref={revealRef as React.RefObject<HTMLElement>}
      className="relative flex h-full w-screen shrink-0 snap-start snap-always overflow-y-auto"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col justify-center px-4 pb-20 pt-20 sm:px-6 lg:px-10">
        {children}
      </div>
    </section>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ eyebrow, title, lead }: { eyebrow: string; title: string; lead: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center lg:mx-0 lg:text-left">
      <div className="reveal mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-green-300 backdrop-blur">
        {eyebrow}
      </div>
      <h2 className="reveal font-serif text-3xl leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl" data-delay="1">
        {title}
      </h2>
      <p className="reveal mt-4 text-base leading-relaxed text-white/70 sm:mt-5 sm:text-lg" data-delay="2">{lead}</p>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────
function SectionHome({ id, onCta }: { id: string; onCta: () => void }) {
  const revealRef = useReveal();
  return (
    <section
      id={id}
      ref={revealRef as React.RefObject<HTMLElement>}
      className="relative flex h-full w-screen shrink-0 snap-start snap-always overflow-hidden"
    >
      <img src={heroDesktop} alt="Lush green landscape" className="absolute inset-0 h-full w-full object-cover object-center" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/10 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center px-4 pb-20 pt-20 sm:px-6 lg:px-10">
        <div className="w-full max-w-xl text-left">
          <div className="reveal mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-green-300" />
            <span>Open education, powered by AI</span>
          </div>
          <h1 className="reveal font-serif text-[2.5rem] leading-[1.05] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.25rem]" data-delay="1">
            Open education for{" "}
            <span className="bg-gradient-to-r from-green-300 to-emerald-200 bg-clip-text text-transparent">everyone</span>
            , powered by AI
          </h1>
          <p className="reveal mt-5 max-w-md text-base leading-relaxed text-white/70 sm:mt-7" data-delay="2">
            We combine the power of community and artificial intelligence to make learning open, accessible, and inspiring for all.
          </p>
          <div className="reveal mt-6 flex flex-col items-start gap-3 sm:mt-8 sm:flex-row sm:items-center" data-delay="3">
            <button
              onClick={onCta}
              className="inline-flex items-center gap-2 rounded-full bg-black px-7 py-3.5 text-sm font-medium text-white transition hover:bg-black/80"
            >
              Join the Movement <ArrowRight className="h-4 w-4" />
            </button>
            <span className="text-xs text-white/50">Free &amp; open for everyone</span>
          </div>
        </div>

        {/* Floating cards — desktop */}
        <div className="pointer-events-none absolute right-4 top-0 hidden h-full w-[45%] lg:block xl:right-10">
          <Card variant="feature" className="reveal-right absolute right-8 top-[12%]" icon={<Sparkles className="h-5 w-5 text-purple-300" />} iconBg="bg-white/20" title="AI Tutor" body="Personalized help for every learner." light data-delay="1" />
          <Card variant="feature" className="reveal-right absolute left-0 top-[35%]" icon={<BookOpen className="h-5 w-5 text-blue-300" />} iconBg="bg-white/20" title="Open Resources" body="Share, discover, and grow together." light data-delay="2" />
          <Card variant="feature" className="reveal-right absolute bottom-[28%] left-10" icon={<Languages className="h-5 w-5 text-green-300" />} iconBg="bg-white/20" title="AI Translation" body="Break language barriers." light data-delay="3" />
          <Card variant="feature" className="reveal-right absolute bottom-[10%] right-4" icon={<ShieldCheck className="h-5 w-5 text-amber-300" />} iconBg="bg-white/20" title="Open &amp; Ethical" body="Built for transparency, privacy, and fairness." light data-delay="4" />
        </div>

        {/* Floating cards — mobile */}
        <div className="pointer-events-none absolute bottom-14 left-4 right-4 flex justify-between lg:hidden">
          <Card variant="feature" className="reveal" icon={<Sparkles className="h-5 w-5 text-purple-300" />} iconBg="bg-white/20" title="AI Tutor" body="Personalized help for every learner." light data-delay="4" />
          <Card variant="feature" className="reveal" icon={<Languages className="h-5 w-5 text-green-300" />} iconBg="bg-white/20" title="AI Translation" body="Break language barriers." light data-delay="5" />
        </div>
      </div>
    </section>
  );
}

function SectionMission({ id }: { id: string }) {
  return (
    <Section id={id}>
      <SectionHeader eyebrow="Our Mission" title="Learning should be open, not gated." lead="We believe every learner — regardless of geography, income, or language — deserves access to high-quality education. OpenLearn AI brings together an open community and modern AI to make that a reality." />
      <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3">
        <div className="reveal group" data-delay="1"><Card variant="info" icon={<BookOpen className="h-5 w-5" />} title="Access for all" body="Free, open resources anyone can use, remix, and share." /></div>
        <div className="reveal group" data-delay="2"><Card variant="info" icon={<Sparkles className="h-5 w-5" />} title="AI as a helper" body="Assistive AI that empowers people — never replaces teachers." /></div>
        <div className="reveal group" data-delay="3"><Card variant="info" icon={<ShieldCheck className="h-5 w-5" />} title="Ethical by design" body="Transparent, privacy-respecting, and inclusive by default." /></div>
      </div>
    </Section>
  );
}

function SectionEducators({ id }: { id: string }) {
  return (
    <Section id={id}>
      <SectionHeader eyebrow="For Educators" title="Tools that give teachers back their time." lead="Plan lessons, translate materials, and personalize learning paths in minutes. Focus on your students — let AI handle the busywork." />
      <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-4">
        <div className="reveal group" data-delay="1"><Card variant="info" icon={<Sparkles className="h-5 w-5" />} title="Lesson co-pilot" body="Generate outlines, quizzes, and rubrics aligned to your goals." /></div>
        <div className="reveal group" data-delay="2"><Card variant="info" icon={<Languages className="h-5 w-5" />} title="Instant translation" body="Deliver your materials in dozens of languages, in a click." /></div>
        <div className="reveal group" data-delay="3"><Card variant="info" icon={<Users className="h-5 w-5" />} title="Shared classrooms" body="Collaborate with other educators on open, reusable content." /></div>
        <div className="reveal group" data-delay="4"><Card variant="info" icon={<BookOpen className="h-5 w-5" />} title="Open library" body="Publish your best resources to a global commons." /></div>
      </div>
    </Section>
  );
}

function SectionLearners({ id }: { id: string }) {
  return (
    <Section id={id}>
      <SectionHeader eyebrow="For Learners" title="Your curiosity, your pace, your path." lead="Get a patient AI tutor that meets you where you are, in the language you think in — and connect with peers learning alongside you." />
      <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3">
        <div className="reveal group" data-delay="1"><Card variant="info" icon={<Sparkles className="h-5 w-5" />} title="Personal AI tutor" body="Ask anything. Get clear explanations, examples, and practice." /></div>
        <div className="reveal group" data-delay="2"><Card variant="info" icon={<BookOpen className="h-5 w-5" />} title="Open courses" body="Explore thousands of free lessons across every subject." /></div>
        <div className="reveal group" data-delay="3"><Card variant="info" icon={<Users className="h-5 w-5" />} title="Study circles" body="Join small groups of learners chasing the same goals." /></div>
      </div>
    </Section>
  );
}

function SectionCommunity({ id }: { id: string }) {
  return (
    <Section id={id}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
        <div className="flex-1">
          <SectionHeader eyebrow="Community" title="Built with — not just for — its people." lead="Educators, learners, translators, and technologists shaping OpenLearn AI together. Join the movement to keep learning open for the next generation." />
          <div className="reveal mt-8 sm:mt-10" data-delay="4">
            <a href="mailto:hello@openlearn.ai" className="inline-flex items-center gap-2 rounded-full bg-black px-7 py-3.5 text-sm font-medium text-white transition hover:bg-black/80">
              Join the Movement <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
        <div className="mt-8 grid gap-4 lg:mt-0 lg:w-[380px] lg:shrink-0">
          <div className="reveal group" data-delay="1"><Card variant="info" icon={<Users className="h-5 w-5" />} title="Contribute" body="Add courses, review content, translate lessons, mentor peers." /></div>
          <div className="reveal group" data-delay="2"><Card variant="info" icon={<Languages className="h-5 w-5" />} title="Translate" body="Help bring lessons into your language and dialect." /></div>
          <div className="reveal group" data-delay="3"><Card variant="info" icon={<ShieldCheck className="h-5 w-5" />} title="Govern openly" body="Have a voice in how the platform grows and stays trustworthy." /></div>
        </div>
      </div>
    </Section>
  );
}

function SectionAbout({ id }: { id: string }) {
  return (
    <Section id={id}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
        <div className="flex-1">
          <SectionHeader eyebrow="About Us" title="A small team with a wide-open mission." lead="OpenLearn AI is a nonprofit-inspired collective of educators, engineers, and researchers building open infrastructure for the future of learning." />
          <div className="reveal mt-8 flex flex-wrap gap-8 sm:mt-10" data-delay="4">
            {[["100%", "Open source"], ["50+", "Languages supported"], ["∞", "Free for learners"]].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="font-serif text-4xl font-bold text-green-300 sm:text-5xl">{val}</div>
                <div className="mt-1 text-sm text-white/60">{label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8 grid gap-4 lg:mt-0 lg:w-[380px] lg:shrink-0">
          <div className="reveal group" data-delay="1"><Card variant="info" icon={<BookOpen className="h-5 w-5" />} title="Open" body="Everything we build is designed to be shared and improved." /></div>
          <div className="reveal group" data-delay="2"><Card variant="info" icon={<ShieldCheck className="h-5 w-5" />} title="Trustworthy" body="Privacy-first, transparent, and independent of ad models." /></div>
          <div className="reveal group" data-delay="3"><Card variant="info" icon={<Sparkles className="h-5 w-5" />} title="Human-first" body="AI serves people — teachers, learners, and communities." /></div>
        </div>
      </div>
    </Section>
  );
}
